import React, { useEffect, useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';

import DeduplicateLeadDialog from './DeduplicateLeadDialog';
import LeadFiles from './LeadFiles';
import LeadForm from './LeadForm';
import LeadComments from './LeadComments';
import LeadSummaryInfo from './LeadSummaryInfo';
import { useGetLeadsQuery, useUpdateLeadMutation } from '../../redux/services/leadApi';

const useSeenUpdate = ({lead, isOpen}) => {
  const leadId = lead?.id;
  // const leadSeen = lead?.seen;
  const [update] = useUpdateLeadMutation();
  useEffect(() => {
    const params = {id: leadId, body: {seen: true}};
    if (leadId && isOpen) update(params);
  }, [leadId, isOpen]);
};

const LeadDialog = props => {
  const { lead, dialogIsOpen, onClose, afterEdit } = props;
  const [duplicationModalOpen, setDuplicationModalOpen] = useState(false);
  useSeenUpdate({lead, isOpen: dialogIsOpen});
  return (
    <Dialog
      open={dialogIsOpen}
      fullWidth={true}
      maxWidth='lg'
      onClose={onClose}
    >
      <DialogContent style={{padding: 0}}>
        <Grid container padding={0}>
          <Grid item xs={12} md={8} padding={2} paddingTop={4}>
            <Stack 
              direction='row' 
              justifyContent='flex-end' 
              spacing={1} 
              alignItems='center'
              sx={{display: {xs: 'flex', md: 'none'}}}
            >
              <IconButton
                onClick={props.onClose}
              >
                <CloseIcon />
              </IconButton>
            </Stack> 
            {
              lead.is_duplicate_email && lead.status !== 'ARCHIVED' &&
              <>
                <DeduplicateLeadDialog 
                  email={lead.contact_email}
                  dialogIsOpen={duplicationModalOpen}
                  onClose={() => setDuplicationModalOpen(false)}
                />
                <Alert severity='error' variant='outlined' sx={{marginBottom: 2}}>
                  <Stack direction='row' alignItems='center' gap={1}>
                    This lead is likely a duplicate of another lead.
                    <Button 
                      color='error' 
                      variant='contained' 
                      size='small' 
                      onClick={() => setDuplicationModalOpen(true)}
                    >
                      Resolve Duplicates
                    </Button>
                  </Stack>
                </Alert>
              </>
            }
            <LeadForm
              editing={true}
              lead={lead}
              afterEdit={afterEdit}
            />
          </Grid>
          {/* Side panel */}
          <Grid item xs={12} md={4}>
            <Box 
              marginLeft={{xs:0, md: 4}}
              padding={2}
              style={{
                height: '100%',
              }}
              sx={{backgroundColor: {xs: '#f5f5f5', md: '#f5f5f5' },}}
            >
              <Stack spacing={4} marginTop={0}>
                <Stack 
                  direction='row' 
                  justifyContent='flex-end' 
                  alignItems='center' 
                  padding={0} 
                  margin={0}
                >
                  <IconButton
                    onClick={onClose}
                    size='small'
                  >
                    <CloseIcon />
                  </IconButton>
                </Stack>
                <LeadSummaryInfo lead={lead} />
                <LeadFiles lead={lead} />
                <LeadComments lead={lead} />
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

const LeadDialogContainer = props => {
  const { leadId, lead, dialogIsOpen} = props;
  const leadQuery = useGetLeadsQuery({id: leadId}, {skip: !!lead || !leadId});
  const _lead = lead || leadQuery?.data?.data?.[0];
  if (!_lead || !dialogIsOpen) return;
  if (leadId && _lead.id !== leadId) return;
  return <LeadDialog {...props} lead={_lead} />;
};

export default LeadDialogContainer;